import React, {
 useState
} from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 ArrowLeft, Sparkles, Loader2
} from 'lucide-react';
import {
 ParsedSpec
} from '../utils/specParser';

interface AIDescribeConnectionProps {
    onBack: () => void;
    onAIResult: (spec: ParsedSpec) => void;
}

const examplePrompts = [
    'Stripe API for payments',
    'PostgreSQL database on AWS RDS',
    'My company\'s internal REST API with OAuth2',
];

function mockAIResponse(text: string): ParsedSpec {
    const lower = text.toLowerCase();

    if (lower.includes('stripe') || lower.includes('payment')) {
        return {
            sourceType: 'rest',
            connectionName: 'Stripe API',
            description: 'Stripe payment processing API',
            baseUrl: 'https://api.stripe.com/v1',
            authType: 'bearer',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 base_url: 'https://api.stripe.com/v1'
},
        };
    }

    if (lower.includes('postgres') || lower.includes('postgresql')) {
        return {
            sourceType: 'postgresql',
            connectionName: 'PostgreSQL Database',
            description: text,
            baseUrl: '',
            authType: 'none',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 host: 'localhost',
port: 5432,
database: 'mydb'
},
        };
    }

    if (lower.includes('mysql')) {
        return {
            sourceType: 'mysql',
            connectionName: 'MySQL Database',
            description: text,
            baseUrl: '',
            authType: 'none',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 host: 'localhost',
port: 3306,
database: 'mydb'
},
        };
    }

    if (lower.includes('mongo')) {
        return {
            sourceType: 'mongodb',
            connectionName: 'MongoDB Database',
            description: text,
            baseUrl: '',
            authType: 'none',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 host: 'localhost',
port: 27017
},
        };
    }

    if (lower.includes('redis')) {
        return {
            sourceType: 'redis',
            connectionName: 'Redis Cache',
            description: text,
            baseUrl: '',
            authType: 'none',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 host: 'localhost',
port: 6379
},
        };
    }

    if (lower.includes('oauth')) {
        return {
            sourceType: 'rest',
            connectionName: 'REST API',
            description: text,
            baseUrl: 'https://api.example.com',
            authType: 'oauth2',
            authDetails: {},
            endpointCount: 0,
            fileName: '',
            settings: {
 base_url: 'https://api.example.com'
},
        };
    }

    // Default
    return {
        sourceType: 'rest',
        connectionName: 'API Connection',
        description: text,
        baseUrl: 'https://api.example.com',
        authType: 'none',
        authDetails: {},
        endpointCount: 0,
        fileName: '',
        settings: {
 base_url: 'https://api.example.com'
},
    };
}

const AIDescribeConnection: React.FC<AIDescribeConnectionProps> = ({
 onBack, onAIResult
}) => {
    const [description, setDescription] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleSubmit = async () => {
        if (!description.trim()) return;
        setIsProcessing(true);

        // Simulate AI processing delay
        await new Promise((resolve) => setTimeout(resolve, 2000));

        const result = mockAIResponse(description);
        onAIResult(result);
    };

    return (
        <div className="flex flex-col gap-6 max-w-3xl mx-auto py-8">
            <div>
                <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Sparkles className="h-6 w-6" />
                    Describe with AI
                </h2>
                <p className="text-muted-foreground mt-1">
                    Tell us about the service you want to connect to, and we'll configure it for you.
                </p>
            </div>

            <div className="space-y-4">
                <Textarea
                    placeholder="Describe the service you want to connect to..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[160px] text-base resize-none"
                    disabled={isProcessing}
                />

                <div className="flex flex-wrap gap-2">
                    <span className="text-sm text-muted-foreground mr-1 self-center">Try:</span>
                    {examplePrompts.map((prompt) => (
                        <Badge
                            key={prompt}
                            variant="outline"
                            className="cursor-pointer hover:bg-muted transition-colors px-3 py-1.5 text-sm"
                            onClick={() => !isProcessing && setDescription(prompt)}
                        >
                            {prompt}
                        </Badge>
                    ))}
                </div>
            </div>

            <Button
                onClick={handleSubmit}
                disabled={!description.trim() || isProcessing}
                className="w-full"
                size="lg"
            >
                {isProcessing ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Analyzing your description...
                    </>
                ) : (
                    <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Connection
                    </>
                )}
            </Button>
        </div>
    );
};

export default AIDescribeConnection;
